import React, { useState, useEffect } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";

const CLASS_TYPES = ["Yoga", "HIIT", "Strength", "Cardio", "Pilates", "Zumba", "CrossFit", "Spinning", "Boxing", "General"];
const RECURRENCE_OPTIONS = [
  { value: "one_time", label: "One-time" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
];

const emptyForm = {
  name: "",
  class_type: "General",
  trainer_id: "",
  branch_id: "",
  start_date: new Date()?.toISOString()?.split("T")?.[0],
  start_time: "09:00",
  end_time: "10:00",
  recurrence: "one_time",
  max_capacity: 20,
  description: "",
};

export default function AddEditClassModal({ open, onClose, onSaved, editClass }) {
  useAuth();
  const [form, setForm] = useState(emptyForm);
  const [trainers, setTrainers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [errors, setErrors] = useState({});
  const [enrollmentCount, setEnrollmentCount] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetchTrainersAndBranches();
    if (editClass) {
      setForm({
        name: editClass?.name || "",
        class_type: editClass?.class_type || "General",
        trainer_id: editClass?.trainer_id || "",
        branch_id: editClass?.branch_id || "",
        start_date: editClass?.start_date || new Date()?.toISOString()?.split("T")?.[0],
        start_time: editClass?.start_time?.slice(0, 5) || "09:00",
        end_time: editClass?.end_time?.slice(0, 5) || "10:00",
        recurrence: editClass?.recurrence || "one_time",
        max_capacity: editClass?.max_capacity || 20,
        description: editClass?.description || "",
      });
      setEnrollmentCount(editClass?.enrolled_count || 0);
    } else {
      setForm(emptyForm);
      setEnrollmentCount(0);
    }
    setErrors({});
    setShowDeleteConfirm(false);
  }, [open, editClass]);

  const fetchTrainersAndBranches = async () => {
    try {
      const [trainerResponse, branchResponse] = await Promise.all([
        api.get('/staff', { params: { role: 'trainer' } }),
        api.get('/branches'),
      ]);
      setTrainers(trainerResponse?.data || []);
      setBranches(branchResponse?.data || []);
    } catch (err) {
      console.error("Failed to load trainers/branches:", err);
    }
  };

  const validate = () => {
    const errs = {};
    if (!form?.name?.trim()) errs.name = "Class name is required";
    if (!form?.start_date) errs.start_date = "Start date is required";
    if (!form?.start_time) errs.start_time = "Start time is required";
    if (!form?.end_time) errs.end_time = "End time is required";
    if (form?.start_time >= form?.end_time) errs.end_time = "End time must be after start time";
    if (!form?.max_capacity || form?.max_capacity < 1) errs.max_capacity = "Capacity must be at least 1";
    setErrors(errs);
    return Object.keys(errs)?.length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        name: form?.name?.trim(),
        class_type: form?.class_type,
        trainer_id: form?.trainer_id || null,
        branch_id: form?.branch_id || null,
        start_date: form?.start_date,
        start_time: form?.start_time,
        end_time: form?.end_time,
        recurrence: form?.recurrence,
        max_capacity: parseInt(form?.max_capacity),
        description: form?.description?.trim() || null,
      };

      if (editClass?.id) {
        await api.put(`/classes/${editClass?.id}`, payload);
      } else {
        await api.post('/classes', payload);
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      setErrors({ submit: err?.message || "Failed to save class" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (enrollmentCount > 0) {
      setErrors({ submit: `Cannot delete: ${enrollmentCount} member(s) are enrolled in this class.` });
      setShowDeleteConfirm(false);
      return;
    }
    setDeleting(true);
    try {
      await api.delete(`/classes/${editClass?.id}`);
      onSaved?.();
      onClose?.();
    } catch (err) {
      setErrors({ submit: err?.message || "Failed to delete class" });
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-[var(--color-card)] rounded-xl border border-[var(--color-border)] shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[var(--color-primary)]/15 flex items-center justify-center">
              <Icon name="CalendarDays" size={16} color="var(--color-primary)" />
            </div>
            <h2 className="text-base font-bold text-[var(--color-foreground)]">
              {editClass ? "Edit Class" : "Add New Class"}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-[var(--color-muted)] transition-colors">
            <Icon name="X" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>

        {/* Form */}
        <div className="p-5 space-y-4">
          {errors?.submit && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <Icon name="AlertCircle" size={14} color="#ef4444" />
              {errors?.submit}
            </div>
          )}

          {/* Class Name */}
          <div>
            <label className="block text-xs font-medium text-[var(--color-foreground)] mb-1">Class Name *</label>
            <input
              value={form?.name}
              onChange={e => set("name", e?.target?.value)}
              placeholder="e.g. Morning Yoga"
              className={`w-full px-3 py-2 text-sm border rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] ${errors?.name ? "border-red-400" : "border-[var(--color-border)]"}`}
            />
            {errors?.name && <p className="text-xs text-red-500 mt-1">{errors?.name}</p>}
          </div>

          {/* Class Type */}
          <div>
            <label className="block text-xs font-medium text-[var(--color-foreground)] mb-1">Class Type</label>
            <select
              value={form?.class_type}
              onChange={e => set("class_type", e?.target?.value)}
              className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
            >
              {CLASS_TYPES?.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {/* Trainer & Branch */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[var(--color-foreground)] mb-1">Trainer</label>
              <select
                value={form?.trainer_id}
                onChange={e => set("trainer_id", e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              >
                <option value="">No Trainer</option>
                {trainers?.map(t => <option key={t?.id} value={t?.id}>{t?.full_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--color-foreground)] mb-1">Branch</label>
              <select
                value={form?.branch_id}
                onChange={e => set("branch_id", e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              >
                <option value="">All Branches</option>
                {branches?.map(b => <option key={b?.id} value={b?.id}>{b?.name}</option>)}
              </select>
            </div>
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-xs font-medium text-[var(--color-foreground)] mb-1">Start Date *</label>
            <input
              type="date"
              value={form?.start_date}
              onChange={e => set("start_date", e?.target?.value)}
              className={`w-full px-3 py-2 text-sm border rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] ${errors?.start_date ? "border-red-400" : "border-[var(--color-border)]"}`}
            />
            {errors?.start_date && <p className="text-xs text-red-500 mt-1">{errors?.start_date}</p>}
          </div>

          {/* Start & End Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[var(--color-foreground)] mb-1">Start Time *</label>
              <input
                type="time"
                value={form?.start_time}
                onChange={e => set("start_time", e?.target?.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] ${errors?.start_time ? "border-red-400" : "border-[var(--color-border)]"}`}
              />
              {errors?.start_time && <p className="text-xs text-red-500 mt-1">{errors?.start_time}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--color-foreground)] mb-1">End Time *</label>
              <input
                type="time"
                value={form?.end_time}
                onChange={e => set("end_time", e?.target?.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] ${errors?.end_time ? "border-red-400" : "border-[var(--color-border)]"}`}
              />
              {errors?.end_time && <p className="text-xs text-red-500 mt-1">{errors?.end_time}</p>}
            </div>
          </div>

          {/* Recurrence & Capacity */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[var(--color-foreground)] mb-1">Recurrence</label>
              <select
                value={form?.recurrence}
                onChange={e => set("recurrence", e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              >
                {RECURRENCE_OPTIONS?.map(r => <option key={r?.value} value={r?.value}>{r?.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--color-foreground)] mb-1">Max Capacity *</label>
              <input
                type="number"
                min="1"
                value={form?.max_capacity}
                onChange={e => set("max_capacity", e?.target?.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] ${errors?.max_capacity ? "border-red-400" : "border-[var(--color-border)]"}`}
              />
              {errors?.max_capacity && <p className="text-xs text-red-500 mt-1">{errors?.max_capacity}</p>}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-[var(--color-foreground)] mb-1">Description</label>
            <textarea
              value={form?.description}
              onChange={e => set("description", e?.target?.value)}
              rows={2}
              placeholder="Optional class description..."
              className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-[var(--color-border)] gap-2">
          <div>
            {editClass && !showDeleteConfirm && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
              >
                <Icon name="Trash2" size={13} color="#dc2626" />
                Delete
              </button>
            )}
            {showDeleteConfirm && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-red-600 font-medium">Confirm delete?</span>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-3 py-1.5 text-xs font-medium bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50"
                >
                  {deleting ? "Deleting..." : "Yes, Delete"}
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-3 py-1.5 text-xs font-medium border border-[var(--color-border)] rounded-md hover:bg-[var(--color-muted)]"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm border border-[var(--color-border)] rounded-md text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] disabled:opacity-50 transition-colors"
            >
              {saving ? <><Icon name="Loader2" size={14} color="white" className="animate-spin" />Saving...</> : <><Icon name="Save" size={14} color="white" />{editClass ? "Update" : "Create"} Class</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
